import { PartnerType } from "@tm/isomorphic-utils";
import Head from "next/head";

interface HeadTags {
  title: string;
  canonical: string;
  metaTags?: {
    name: string;
    content: string;
  }[];
  schema?: any;
}

interface FaqData {
  title?: string;
  faqList?: {
    ques?: { text?: { value?: string } } | string;
    info?: string;
  }[];
}

export function withSeoHeadTags(
  component: JSX.Element,
  headTags: HeadTags,
  partner: PartnerType,
  longImageUrl: string,
  faq?: FaqData
) {
  const faqSchemaMainEntity =
    faq?.faqList
      ?.map((faqItem) => {
        const question =
          typeof faqItem?.ques === "string"
            ? faqItem.ques
            : faqItem?.ques?.text?.value;
        const answer = faqItem?.info;

        if (!question || !answer) {
          return null;
        }

        return {
          "@type": "Question",
          name: question,
          acceptedAnswer: {
            "@type": "Answer",
            text: answer,
          },
        };
      })
      .filter(Boolean) || [];

  const faqSchema = faqSchemaMainEntity.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        ...(faq?.title ? { name: faq.title } : {}),
        mainEntity: faqSchemaMainEntity,
      }
    : null;

  return (
    <>
      <Head>
        <title>{headTags?.title || "Travel Insurance - MakeMyTrip"}</title>

        {headTags?.metaTags?.map((metaTag, index) => {
          if (metaTag.name && metaTag.content) {
            return (
              <meta key={index} name={metaTag.name} content={metaTag.content} />
            );
          }
        })}

        {headTags?.title && (
          <meta property="og:title" content={headTags?.title} />
        )}
        {headTags?.metaTags?.find((meta) => meta.name === "description")
          ?.content && (
          <meta
            property="og:description"
            content={
              headTags?.metaTags?.find((meta) => meta.name === "description")
                ?.content
            }
          />
        )}
        {longImageUrl && (
          <>
            <meta property="og:image" content={longImageUrl} />
          </>
        )}
        {headTags?.title && (
          <meta name="twitter:title" content={headTags?.title} />
        )}
        {headTags?.metaTags?.find((meta) => meta.name === "description")
          ?.content && (
          <meta
            name="twitter:description"
            content={
              headTags?.metaTags?.find((meta) => meta.name === "description")
                ?.content
            }
          />
        )}
        {longImageUrl && <meta name="twitter:image" content={longImageUrl} />}

        {headTags?.schema?.map((schema, index) => {
          return (
            <script
              type="application/ld+json"
              key={`schema-${index}`}
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(schema),
              }}
            />
          );
        })}
        {faqSchema && (
          <script
            type="application/ld+json"
            key="schema-faq"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(faqSchema),
            }}
          />
        )}

        {headTags?.canonical ? (
          <link rel="canonical" href={headTags.canonical} />
        ) : null}
      </Head>
      {component}
    </>
  );
}
